import { useState } from "react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2 } from "lucide-react";

interface ConfirmDeleteDoubleProps {
  onConfirm: () => void | Promise<void>;
  title?: string;
  description?: string;
  /** The name the user must type to confirm */
  confirmText: string;
  trigger?: React.ReactNode;
  triggerClassName?: string;
  triggerSize?: "default" | "sm" | "lg" | "icon";
  triggerVariant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";
  disabled?: boolean;
}

export function ConfirmDeleteDouble({
  onConfirm,
  title = "Are you sure?",
  description = "This action cannot be undone.",
  confirmText,
  trigger,
  triggerClassName,
  triggerSize = "icon",
  triggerVariant = "ghost",
  disabled = false,
}: ConfirmDeleteDoubleProps) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<1 | 2>(1);
  const [typed, setTyped] = useState("");
  const [loading, setLoading] = useState(false);

  const handleOpenChange = (v: boolean) => {
    setOpen(v);
    if (!v) {
      setStep(1);
      setTyped("");
      setLoading(false);
    }
  };

  const handleFirstConfirm = () => {
    setStep(2);
  };

  const handleFinalConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
      handleOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  const isMatch = typed.trim().toLowerCase() === confirmText.trim().toLowerCase();

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogTrigger asChild>
        {trigger || (
          <Button
            variant={triggerVariant}
            size={triggerSize}
            className={triggerClassName || "text-destructive hover:text-destructive"}
            disabled={disabled}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        )}
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{step === 1 ? title : "Final Confirmation"}</AlertDialogTitle>
          <AlertDialogDescription>
            {step === 1
              ? description
              : (
                <span className="space-y-2 block">
                  <span className="block">Type <strong className="text-foreground">"{confirmText}"</strong> to permanently confirm.</span>
                </span>
              )}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {step === 2 && (
          <Input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={confirmText}
            className="mt-1"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter" && isMatch && !loading) handleFinalConfirm();
            }}
          />
        )}

        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          {step === 1 ? (
            <Button
              variant="destructive"
              onClick={handleFirstConfirm}
            >
              Continue
            </Button>
          ) : (
            <Button
              variant="destructive"
              disabled={!isMatch || loading}
              onClick={handleFinalConfirm}
            >
              {loading ? "Deleting…" : "Delete Forever"}
            </Button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
